import React from 'react';
import {
    InputField,
    CheckboxField,
    DropdownSelectField,
    TextAreaField,
    Button
} from '@jutro/components';
import AdacDatePicker from '../AdacDatePicker/AdacDatePicker';
import styles from './ElementMapping.module.scss';
import { parseJSONStringToObject } from './ElementMappingUtil';

const renderNode = (
    element,
    formState,
    handleChange,
    validationErrors = {},
    externalDateTouched = false
) => {

    if (!element || !element.tagName) {
        // If element or tagName is not defined, return null to avoid errors
        return null;
    }
    const getValidationMessage = (validatedElement) => {
        return validationErrors[validatedElement.name] || null;
    };

    const showError = Boolean(getValidationMessage(element));

    // Populate formState with initial value if the element has a value
    if (element.value && !(element.name in formState)) {
        // Checkbox requires special handling
        if (element.type === 'checkbox' && element.checked === true) {
            handleChange(element.name, true);
        } else if (element.type === 'checkbox' && element.value === 'on') {
            handleChange(element.name, false);
        } else {
            handleChange(element.name, element.value);
        }
    }

    switch (element.tagName.toLowerCase()) {
        case 'input':
            switch (element.type) {
                case 'text':
                case 'email':
                    return (
                        <InputField
                            key={element.getAttribute('uniqueId')}
                            id={element.id}
                            name={element.name}
                            label={element.getAttribute('label')}
                            required={element.required}
                            value={formState[element.name]}
                            maxLength={Number(element.getAttribute('maxLength')) || undefined}
                            disabled={element.readOnly}
                            onValueChange={(value) => handleChange(element.name, value)}
                            validationMessages={showError ? [getValidationMessage(element)] : []}
                            showErrors={showError}
                            className={styles.input}
                        />
                    );
                case 'date': {
                    const validationRules = parseJSONStringToObject(element.getAttribute('validateDate') || '{}');

                    return (
                        <AdacDatePicker
                            key={element.getAttribute('uniqueId')}
                            date={formState[element.name] || null}
                            setDate={(date) => handleChange(element.name, date)}
                            label={element.getAttribute('label')}
                            placeholder={element.placeholder}
                            isValid={!(getValidationMessage(element)
                                && getValidationMessage(element).trim() !== '')}
                            required={element.required}
                            validationMessage={showError ? getValidationMessage(element) : ''}
                            minYear={validationRules.minDate}
                            maxYear={validationRules.maxDate}
                            externalTouched={externalDateTouched}
                        />
                    );
                }
                case 'checkbox':
                    return (
                        <CheckboxField
                            key={element.getAttribute('uniqueId')}
                            id={element.id}
                            name={element.name}
                            className={styles.checkbox}
                            label={element.getAttribute('label')}
                            required={element.required}
                            checked={element.checked || false}
                            value={formState[element.name] || false}
                            onValueChange={(value) => handleChange(element.name, value)}
                            disabled={element.readOnly}
                            showInlineLabel
                            validationMessages={showError ? [getValidationMessage(element)] : []}
                            showErrors={showError}
                        />
                    );
                default:
                    return null;
            }
        case 'textarea':
            return (
                <TextAreaField
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    name={element.name}
                    className={styles.input}
                    label={element.getAttribute('label')}
                    rows={element.rows}
                    cols={element.cols}
                    maxLength={Number(element.getAttribute('maxLength')) || undefined}
                    value={formState[element.name] || ''}
                    onValueChange={(value) => handleChange(element.name, value)}
                    validationMessages={showError ? [getValidationMessage(element)] : []}
                    showErrors={showError}
                    disabled={element.readOnly}
                />
            );
        case 'select':
            return (
                <DropdownSelectField
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    name={element.name}
                    className={styles.input}
                    label={element.getAttribute('label')}
                    availableValues={[...element.children].map((option) => ({
                        code: option.value,
                        name: option.textContent
                    }))}
                    value={formState[element.name] || ''}
                    onValueChange={(value) => handleChange(element.name, value)}
                    required={element.required}
                    validationMessages={showError ? [getValidationMessage(element)] : []}
                    showErrors={showError}
                />
            );
        case 'table': {
            const columns = [...element.querySelectorAll('thead th')];
            const rows = [...element.querySelectorAll('tbody tr')];

            // Fallback for when headers are not present, calculate number of columns dynamically
            const columnCount = columns.length || (rows[0] ? rows[0].children.length : 0);
            const getColumnWidth = (cells) => {
                return cells.map((cell) => {
                    if (cell.querySelector('input[type="checkbox"]')) {
                        return 'auto'; // Checkboxes should take minimal space
                    }
                    return `${100 / columnCount}%`; // Default to equal width if content is text
                });
            };

            return (
                <div className={styles.tableWrapper}>
                    {/* Header */}
                    {columns.length > 0 && (
                        <div className={styles.tableHeader}>
                            {columns.map((col, colIndex) => (
                                <div
                                    key={`col-${colIndex}-${col.getAttribute('uniqueId')}`}
                                    className={styles.tableHeaderCell}
                                    style={{ width: `${100 / columnCount}%` }}
                                >
                                    {col.textContent}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Body */}
                    <div className={styles.tableBody}>
                        {rows.map((row, rowIndex) => {
                            const cells = [...row.querySelectorAll('td')];

                            // Get dynamic column widths based on content
                            const columnWidths = getColumnWidth(cells);

                            return (
                                <div key={`row-${rowIndex}-${element.getAttribute('uniqueId')}`} className={styles.tableRow}>
                                    {cells.map((cell, cellIndex) => {
                                        const firstChildElement = [...cell.childNodes].find((node) => node.nodeType === 1);

                                        return (
                                            <div
                                                key={`cell-${rowIndex}-${cellIndex}-${cell.getAttribute('uniqueId')}`}
                                                className={styles.tableCell}
                                                style={{ width: columnWidths[cellIndex] }}
                                            >
                                                {firstChildElement && renderNode(firstChildElement, formState, handleChange, validationErrors, externalDateTouched)}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        case 'div': {
            const inlineElements = [...element.children]; // Get all child elements for inline layout
            return (
                <div className={styles.inlineWrapper}>
                    {inlineElements.map((childElement) => {
                        return (
                            <div
                                key={childElement.getAttribute('uniqueId')}
                                className={styles.inlineElement}
                            >
                                {renderNode(childElement, formState, handleChange, validationErrors, externalDateTouched)}
                            </div>
                        );
                    })}
                </div>
            );
        }
        case 'ul': {
            const listItems = [...element.children];

            return (
                <ul className={styles.ulWrapper}>
                    {listItems.map((childElement,) => {
                        return (
                            <li key={childElement.getAttribute('uniqueId')} className={styles.liElement}>
                                {renderNode(childElement, formState, handleChange, validationErrors, externalDateTouched)}
                            </li>
                        );
                    })}
                </ul>
            );
        }

        case 'li': {
            const liChildren = [...element.children];
            const hasCheckbox = liChildren.some((child) => child.tagName.toLowerCase() === 'input' && child.type === 'checkbox');
            const className = hasCheckbox ? 'checkbox' : 'input';
            return (
                <div className={`${styles.liWrapper} ${styles[className]}`}>
                    {liChildren.map((childElement) => {
                        return renderNode(childElement, formState, handleChange, validationErrors, externalDateTouched);
                    })}
                </div>
            );
        }
        case 'button':
            return (
                <Button
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    actionType={element.type}
                >
                    {element.textContent}
                </Button>
            );
        case 'label':
            return null;
        case 'br':
            return <br key={element.getAttribute('uniqueId')} />;
        case 'h1':
            return <h1 key={element.getAttribute('uniqueId')}>{element.textContent}</h1>;
        case 'h2':
            return <h2 key={element.getAttribute('uniqueId')}>{element.textContent}</h2>;
        case 'h3':
            return <h3 key={element.getAttribute('uniqueId')}>{element.textContent}</h3>;
        case 'h4':
            return <h4 key={element.getAttribute('uniqueId')}>{element.textContent}</h4>;
        case 'h5':
            return <h5 key={element.getAttribute('uniqueId')}>{element.textContent}</h5>;
        case 'h6':
            return <h6 key={element.getAttribute('uniqueId')}>{element.textContent}</h6>;
        case 'p':
            return <p key={element.getAttribute('uniqueId')}>{element.textContent}</p>;
        case 'span':
            return <span key={element.getAttribute('uniqueId')}>{element.textContent}</span>;
        default:
            return null;
    }
};

export default renderNode;
